import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { supabase } from "../utils/supabaseClient";
import { ROUTES as ROUTES_DEFAULTS } from "../config/routes";

/** system_settings keys we read for route paths */
const ROUTE_SETTING_KEYS = {
  FULL_CERT_REGISTRATION: "full_cert_registration_path",
};

const RouteConfigContext = createContext({
  routes: ROUTES_DEFAULTS,
  fullCertRegistrationPath: (id) =>
    id
      ? `${ROUTES_DEFAULTS.FULL_CERT_REGISTRATION}/${id}`
      : ROUTES_DEFAULTS.FULL_CERT_REGISTRATION,
  isLoading: true,
});

export const useRouteConfig = () => useContext(RouteConfigContext);

const RouteConfigProvider = ({ children }) => {
  const [routes, setRoutes] = useState(ROUTES_DEFAULTS);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      try {
        const { data, error } = await supabase
          .from("system_settings")
          .select("setting_key, setting_value")
          .in("setting_key", Object.values(ROUTE_SETTING_KEYS));

        if (cancelled) return;
        if (error) {
          console.warn("RouteConfig: could not load system_settings, using defaults", error.message);
          setIsLoading(false);
          return;
        }

        const next = { ...ROUTES_DEFAULTS };
        (data || []).forEach((row) => {
          const v = row.setting_value?.trim();
          if (!v) return;
          if (row.setting_key === ROUTE_SETTING_KEYS.FULL_CERT_REGISTRATION) {
            next.FULL_CERT_REGISTRATION = v.startsWith("/") ? v : `/${v}`;
          }
        });
        setRoutes(next);
      } catch (e) {
        if (!cancelled) console.warn("RouteConfig: load failed", e);
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };

    load();
    return () => { cancelled = true; };
  }, []);

  const fullCertRegistrationPath = useCallback(
    (id) =>
      id ? `${routes.FULL_CERT_REGISTRATION}/${id}` : routes.FULL_CERT_REGISTRATION,
    [routes.FULL_CERT_REGISTRATION]
  );

  const value = {
    routes,
    fullCertRegistrationPath,
    isLoading,
  };

  return (
    <RouteConfigContext.Provider value={value}>
      {children}
    </RouteConfigContext.Provider>
  );
};

export default RouteConfigProvider;
